import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  timer,
} from 'rxjs';
import { LastValues, ValuesChanges } from '@prlw/core/monitor/types/main-table';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { first, startWith, takeUntil } from 'rxjs/operators';
import { MainTableInstrumentsService } from '@prlw/core/monitor/main-table-instruments.service';

const TICKER_INTERVAL = 5000;
const CHANGES_HIGHLIGHT_DELAY = 2000;

function getValueChange(
  newValue: number | null,
  oldValue: number | null,
): boolean | null {
  if (!newValue || !oldValue || oldValue === newValue) {
    return null;
  }
  return newValue > oldValue;
}

@Injectable({
  providedIn: 'root',
})
export class MainTableHighlightService implements OnDestroy {
  private readonly _valuesChanges$ = new BehaviorSubject<ValuesChanges>({});
  private _lastValues: LastValues = {};

  private readonly destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(mainTableInstrumentsService: MainTableInstrumentsService) {
    combineLatest([
      mainTableInstrumentsService.instruments$,
      timer(0, TICKER_INTERVAL).pipe(startWith(0)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([instruments, time]) => {
        if (time) {
          this._setChanges(instruments);
        } else {
          this._resetValuesChanges();
        }
        this._setLastValues(instruments);
      });
  }

  public get valuesChanges$(): Observable<ValuesChanges> {
    return this._valuesChanges$.asObservable();
  }

  private _setLastValues(instruments: Instrument[]): void {
    this._lastValues = instruments.reduce(
      (a, instrument) => ({
        ...a,
        ...{
          [instrument.instrumentCode]: {
            offer: instrument.quote.sellPrice,
            deal: instrument.quote.lastDealPrice,
            demand: instrument.quote.buyPrice,
          },
        },
      }),
      {},
    );
  }

  private _setChanges(instruments: Instrument[]): void {
    const valuesChanges: ValuesChanges = instruments.reduce((a, instrument) => {
      const { instrumentCode } = instrument;
      const { sellPrice, lastDealPrice, buyPrice } = instrument.quote;
      const {
        offer = null,
        deal = null,
        demand = null,
      } = this._lastValues[instrumentCode] ?? {};
      return {
        ...a,
        ...{
          [instrumentCode]: {
            isOfferUp: getValueChange(sellPrice, offer),
            isDealUp: getValueChange(lastDealPrice, deal),
            isDemandUp: getValueChange(buyPrice, demand),
          },
        },
      };
    }, {});
    this._valuesChanges$.next(valuesChanges);
    this._resetValuesChangesByTime();
  }

  private _resetValuesChanges(): void {
    this._valuesChanges$.next({});
  }

  private _resetValuesChangesByTime(): void {
    timer(CHANGES_HIGHLIGHT_DELAY)
      .pipe(first())
      .subscribe(() => this._resetValuesChanges());
  }
}
